import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { MyWorkOrderListItem } from "../api/types";
import { formatDate, formatWoId, isOverdue } from "../lib/workorders";

export function MaintenanceWorkOrdersPage() {
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const listQ = useQuery({
    queryKey: ["my-work-orders"],
    queryFn: () => api.myWorkOrders.list(),
  });

  const jobs = listQ.data?.workOrders ?? [];

  function toggleDescription(id: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Layout title="My Work Orders">
      <p className="help-line">These are the jobs assigned to you. Tap one to update it.</p>

      {listQ.isLoading ? (
        <p className="sub">Loading...</p>
      ) : listQ.isError ? (
        <section className="card">
          <p>Could not load your work orders. Please try again.</p>
        </section>
      ) : jobs.length === 0 ? (
        <section className="card">
          <p>You have no work orders assigned right now.</p>
        </section>
      ) : (
        <section className="job-list">
          {jobs.map((j) => {
            const isExpanded = expanded.has(j.id);
            return (
              <article
                key={j.id}
                className={"job job-card" + (isOverdue(j.status, j.dueDate) ? " urgent" : "")}
              >
                <div className="job-main">
                  <button
                    type="button"
                    className={"wo-expand-button" + (isExpanded ? " expanded" : "")}
                    onClick={() => toggleDescription(j.id)}
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? "Hide" : "Show"} description for ${formatWoId(j.id)}`}
                  >
                    <span aria-hidden="true" />
                  </button>
                  <div>
                    <span className="job-id">{formatWoId(j.id)}</span>
                    <Link to={`/maintenance/${j.id}`} className="job-title job-title-link">
                      {j.title}
                    </Link>
                    <span className="job-where">
                      {j.buildingName} &middot; {j.where}
                    </span>
                    <span className="job-due">
                      <DueBadge job={j} />
                    </span>
                    {j.timerStartedAt && <span className="badge badge-info">Timer running</span>}
                  </div>
                </div>
                {isExpanded && (
                  <div className="wo-description-panel job-description">
                    <strong>Description</strong>
                    <p>{j.description || "No description provided."}</p>
                  </div>
                )}
              </article>
            );
          })}
        </section>
      )}
    </Layout>
  );
}

function DueBadge({ job }: { job: MyWorkOrderListItem }) {
  if (job.status === "completed") return <span className="badge badge-ok">Completed</span>;
  if (job.status === "cancelled") return <span className="badge badge-muted">Cancelled</span>;
  if (!job.dueDate) return <span className="badge badge-muted">No due date</span>;
  const today = new Date().toISOString().slice(0, 10);
  if (job.dueDate < today) return <span className="badge badge-alert">Overdue</span>;
  if (job.dueDate === today) return <span className="badge badge-alert">Due today</span>;
  return <span className="badge badge-info">Due {formatDate(job.dueDate)}</span>;
}
